import { ChevronDown, type LucideIcon } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../primitives/card';
import { cn } from '../lib/cn';

interface DetailLayoutProps {
  /** The record itself: description, answers, proposals … */
  children: ReactNode;
  /** Status, key facts and actions; sits on the right from `lg`, above the main column on phones */
  aside: ReactNode;
  className?: string;
}

/**
 * Detail page: main column plus a side panel that stays in view while the main column scrolls. It sizes by its own
 * width (a container query), so the same view works as a full page and inside a MasterDetail panel.
 */
export function DetailLayout({ children, aside, className }: DetailLayoutProps) {
  return (
    <div className={cn('@container', className)}>
      <div className="grid items-start gap-4 @2xl:grid-cols-[minmax(0,1fr)_16rem] @4xl:grid-cols-[minmax(0,1fr)_19rem]">
        <div className="grid gap-4 @2xl:sticky @2xl:top-18 @2xl:order-last">{aside}</div>
        <div className="grid min-w-0 gap-4">{children}</div>
      </div>
    </div>
  );
}

interface DetailSectionProps {
  title: string;
  /** Right of the title, e.g. an Edit button */
  action?: ReactNode;
  /** The title becomes a button that shows and hides the content */
  collapsible?: boolean;
  /** With `collapsible`: open at first (default closed) */
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
}

/** A titled card inside a detail page. */
export function DetailSection({
  title,
  action,
  collapsible,
  defaultOpen = false,
  children,
  className,
}: DetailSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();
  if (!collapsible) {
    return (
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>{title}</CardTitle>
          {action}
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    );
  }
  return (
    <Card className={className}>
      <CardHeader className={cn('flex flex-row items-center justify-between gap-3', open ? 'pt-3' : 'py-3')}>
        <CardTitle>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={contentId}
            onClick={() => setOpen((value) => !value)}
            className="flex items-center gap-2 rounded-md text-left focus-visible:outline-2 focus-visible:outline-ring"
          >
            {title}
            <ChevronDown
              aria-hidden
              className={cn(
                'size-4 text-muted-foreground transition-transform motion-reduce:transition-none',
                open && 'rotate-180',
              )}
            />
          </button>
        </CardTitle>
        {action}
      </CardHeader>
      <CardContent id={contentId} className="pt-3" hidden={!open}>
        {children}
      </CardContent>
    </Card>
  );
}

interface FactGridProps {
  items: { icon: LucideIcon; label: string; value: ReactNode }[];
  className?: string;
}

/**
 * Key facts in one card, read at a glance before the details. The facts flow along the row and wrap as needed,
 * so any number of them looks even.
 */
export function FactGrid({ items, className }: FactGridProps) {
  return (
    <dl
      className={cn(
        'flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl bg-surface px-4 py-3 shadow-sm',
        className,
      )}
    >
      {items.map((item) => (
        <div key={item.label} className="flex min-w-0 items-center gap-2.5">
          <span
            aria-hidden
            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-hover"
          >
            <item.icon className="size-4" />
          </span>
          <div className="grid min-w-0">
            <dt className="truncate text-[0.6875rem] text-muted-foreground">{item.label}</dt>
            <dd className="truncate text-sm font-semibold text-secondary">{item.value}</dd>
          </div>
        </div>
      ))}
    </dl>
  );
}

interface DescriptionListProps {
  items: { label: string; value: ReactNode }[];
  className?: string;
}

/** Label and value pairs, e.g. "Postal code · 412 56". */
export function DescriptionList({ items, className }: DescriptionListProps) {
  return (
    <dl
      className={cn(
        'grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-x-4 gap-y-3 text-sm',
        className,
      )}
    >
      {items.map((item) => (
        <div key={item.label} className="grid gap-0.5">
          <dt className="text-xs font-medium text-muted-foreground">{item.label}</dt>
          <dd className="min-w-0 break-words">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
